import React, { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { inject, observer } from 'mobx-react';
import DOMPurify from 'dompurify';
import { getConfig } from '../../lib/configMgr';
import { RequestBuilder } from '../../queries/request';
import CompoundComparison from './CompoundComparison';
import MoleculeImage from '../MoleculeImage';
import { useCompoundFlipAnimation } from './useCompoundFlipAnimation';
import './CompoundComparison.css';

const { ExternalRESTAPI } = getConfig();

// Default highlight color for MCS comparison (Plex Yellow)
const DEFAULT_HIGHLIGHT_COLOR = '#f28d24';
const DEFAULT_HIGHLIGHT_OPACITY = 0.5;

/**
 * CompoundComparisonWithMCS - Shows two compound structures side by side
 * with backend MCS-based highlighting of common substructures
 *
 * Requires backend endpoint: /api/molecule/compare
 * POST body: { smiles1: string, smiles2: string }
 * Response: {
 *   image1: string (base64 or URL),
 *   image2: string (base64 or URL),
 *   mcs: string (SMARTS of MCS)
 * }
 */
const CompoundComparisonWithMCS = ({
  entity1,
  entity2,
  onClose,
  highlightColor = DEFAULT_HIGHLIGHT_COLOR,
  highlightOpacity = DEFAULT_HIGHLIGHT_OPACITY,
  sourceRef1,
  sourceRef2,
  datasets,
  handleCloseRef,
  userStore
}) => {
  const [loading, setLoading] = useState(true);
  const [useFallback, setUseFallback] = useState(false);
  const [mcsImage1, setMcsImage1] = useState(null);
  const [mcsImage2, setMcsImage2] = useState(null);
  const [mcsInfo, setMcsInfo] = useState(null);
  const [mcsOpacity, setMcsOpacity] = useState(0); // Controls crossfade to MCS images
  const containerRef = useRef(null);
  const image1Ref = useRef(null);
  const image2Ref = useRef(null);

  // Use original molecule images for the FLIP animation
  const {
    isClosing,
    containerHeight,
    imagesOpacity,
    handleClose,
    renderAnimationPortal
  } = useCompoundFlipAnimation({
    sourceRef1,
    sourceRef2,
    containerRef,
    image1Ref,
    image2Ref,
    isReady: !useFallback,
    renderClone1: () => (
      <MoleculeImage
        encoding={entity1.smiles || entity1.inchi}
        entity={entity1}
        disableRotation={true}
      />
    ),
    renderClone2: () => (
      <MoleculeImage
        encoding={entity2.smiles || entity2.inchi}
        entity={entity2}
        disableRotation={true}
      />
    ),
    onClose,
    entity1Id: entity1?.id,
    entity2Id: entity2?.id
  });

  // Expose handleClose to parent via ref
  useEffect(() => {
    if (handleCloseRef) {
      handleCloseRef.current = handleClose;
    }
    return () => {
      if (handleCloseRef) {
        handleCloseRef.current = null;
      }
    };
  }, [handleClose, handleCloseRef]);

  useEffect(() => {
    if (!entity1?.smiles || !entity2?.smiles) {
      // Fall back to regular comparison if no SMILES
      console.warn('Missing SMILES for one or both compounds, using fallback rendering');
      setUseFallback(true);
      setLoading(false);
      return;
    }

    // Create AbortController to cancel fetch if component unmounts or effect re-runs
    const abortController = new AbortController();
    let timeoutId = null;

    const fetchComparison = async () => {
      try {
        setLoading(true);

        // Convert hex color to rgba with opacity
        const hexToRgba = (hex, alpha) => {
          const r = parseInt(hex.slice(1, 3), 16);
          const g = parseInt(hex.slice(3, 5), 16);
          const b = parseInt(hex.slice(5, 7), 16);
          return `rgba(${r},${g},${b},${alpha})`;
        };

        // Call backend API to compute MCS and generate highlighted images
        const url = ExternalRESTAPI.MoleculeCompare || '/api/molecule/compare';
        const body = JSON.stringify({
          smiles1: entity1.smiles,
          smiles2: entity2.smiles,
          width: 600,
          height: 600,
          highlightColor: hexToRgba(highlightColor, highlightOpacity),
          format: 'svg'
        });
        const response = await new RequestBuilder(url, { signal: abortController.signal })
          .withAuthorization(userStore.authData)
          .withJSONContentType()
          .asPOST(body)
          .fetch();

        const data = await response.json();

        // Check if we were aborted before updating state
        if (abortController.signal.aborted) {
          return;
        }

        // Backend returns { status, content: { image1, image2, mcs } }
        const content = data.content || data;
        setMcsImage1(content.image1);
        setMcsImage2(content.image2);
        setMcsInfo(content.mcs);
        setLoading(false);

        // Crossfade to MCS images after a brief delay
        timeoutId = setTimeout(() => {
          if (!abortController.signal.aborted) {
            setMcsOpacity(1);
          }
        }, 100);
      } catch (err) {
        if (err.name === 'AbortError') return; // expected on unmount/effect re-run
        console.error('Failed to fetch molecule comparison, falling back to regular rendering:', err);
        // Fall back to regular comparison on error
        if (!abortController.signal.aborted) {
          setUseFallback(true);
          setLoading(false);
        }
      }
    };

    fetchComparison();

    // Cleanup: abort fetch and clear timeout if component unmounts or effect re-runs
    return () => {
      abortController.abort();
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [entity1?.smiles, entity2?.smiles, highlightColor, highlightOpacity]);

  if (!entity1 || !entity2) {
    return null;
  }

  // Use fallback component if backend failed or SMILES missing
  if (useFallback) {
    return (
      <CompoundComparison
        entity1={entity1}
        entity2={entity2}
        onClose={onClose}
        sourceRef1={sourceRef1}
        sourceRef2={sourceRef2}
        datasets={datasets}
      />
    );
  }

  return (
    <div
      ref={containerRef}
      className="compound-comparison"
      style={{
        height: typeof containerHeight === 'number' ? `${containerHeight}px` : 'auto',
        padding: isClosing ? 0 : undefined,
        transition: isClosing ? 'height 0.4s ease-in, padding 0.4s ease-in' : 'none',
        overflow: 'hidden',
        flexShrink: 0
      }}
    >
      <button
        className="comparison-close"
        onClick={handleClose}
        aria-label="Close comparison"
        style={{
          opacity: imagesOpacity,
          transition: 'opacity 0.3s ease-in'
        }}
      >
        ×
      </button>

      {/* Always render content for FLIP animation */}
      <>
          <div className="comparison-container" style={{
            opacity: imagesOpacity,
            transition: 'opacity 0.3s ease-in'
          }}>
            <div className="comparison-item">
              <div className="comparison-image" ref={image1Ref} style={{ position: 'relative' }}>
                {/* Original molecule image - shown during FLIP animation */}
                <div style={{
                  opacity: 1 - mcsOpacity,
                  transition: 'opacity 0.5s ease-in-out',
                  position: mcsImage1 ? 'absolute' : 'relative',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%'
                }}>
                  <MoleculeImage
                    encoding={entity1.smiles || entity1.inchi}
                    entity={entity1}
                    disableRotation={true}
                  />
                </div>
                {/* MCS-highlighted image - crossfades in after loading */}
                {mcsImage1 && (
                  <div style={{
                    opacity: mcsOpacity,
                    transition: 'opacity 0.5s ease-in-out',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%'
                  }}>
                    <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(mcsImage1, { USE_PROFILES: { svg: true, svgFilters: true } }) }} />
                  </div>
                )}
              </div>
              <div className="comparison-label">
                <div className="comparison-title">{entity1.preferredName || entity1.label}</div>
                <div className="comparison-category">{entity1.categoryName}</div>
              </div>
            </div>

            <div className="comparison-item">
              <div className="comparison-image" ref={image2Ref} style={{ position: 'relative' }}>
                {/* Original molecule image - shown during FLIP animation */}
                <div style={{
                  opacity: 1 - mcsOpacity,
                  transition: 'opacity 0.5s ease-in-out',
                  position: mcsImage2 ? 'absolute' : 'relative',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%'
                }}>
                  <MoleculeImage
                    encoding={entity2.smiles || entity2.inchi}
                    entity={entity2}
                    disableRotation={true}
                  />
                </div>
                {/* MCS-highlighted image - crossfades in after loading */}
                {mcsImage2 && (
                  <div style={{
                    opacity: mcsOpacity,
                    transition: 'opacity 0.5s ease-in-out',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%'
                  }}>
                    <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(mcsImage2, { USE_PROFILES: { svg: true, svgFilters: true } }) }} />
                  </div>
                )}
              </div>
              <div className="comparison-label">
                <div className="comparison-title">{entity2.preferredName || entity2.label}</div>
                <div className="comparison-category">{entity2.categoryName}</div>
              </div>
            </div>
          </div>
          {mcsInfo && (
            <div className="mcs-info-box">
              <strong>MCS SMARTS:</strong> <code>{mcsInfo}</code>
            </div>
          )}
        </>

      {/* Loading overlay - shown on top while computing MCS */}
      {loading && (
        <div className="mcs-loading-overlay" style={{ opacity: imagesOpacity }}>
          <div className="mcs-loading-content">
            Computing Maximum Common Substructure...
          </div>
        </div>
      )}

      {renderAnimationPortal()}
    </div>
  );
};

CompoundComparisonWithMCS.propTypes = {
  entity1: PropTypes.object.isRequired,
  entity2: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
  highlightColor: PropTypes.string,
  highlightOpacity: PropTypes.number,
  sourceRef1: PropTypes.object,
  sourceRef2: PropTypes.object,
  datasets: PropTypes.object,
  handleCloseRef: PropTypes.object,
  userStore: PropTypes.object
};

export default inject('userStore')(observer(CompoundComparisonWithMCS));